import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField,
  Typography,
} from '@mui/material';
import {
  Assignment as WorkOrderIcon, NoteAdd as BlankIcon, ConfirmationNumber as TicketIcon,
} from '@mui/icons-material';
import useOrganizationSearch from '../../hooks/useOrganizationSearch';

const FS = '0.8125rem';

const Option = ({ selected, onClick, icon, title, text, disabled, badge }) => (
  <Box
    component="button"
    type="button"
    onClick={disabled ? undefined : onClick}
    sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.25, width: '100%', textAlign: 'left', font: 'inherit', p: 1.25,
      borderRadius: 1, border: '1px solid', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
      borderColor: selected ? 'primary.main' : 'divider', bgcolor: selected ? 'action.selected' : 'background.paper',
      color: 'text.primary', '& .MuiSvgIcon-root': { color: 'primary.main', mt: 0.25 },
    }}
  >
    {icon}
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: FS, fontWeight: 600 }}>{title}</Typography>
        {badge}
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{text}</Typography>
    </Box>
  </Box>
);

/**
 * New Invoice (INVOICE-BY-ITEM.md §6): one entry point for every way of creating an invoice.
 * Props: open, onClose, typingCenterId, thaklisEnabled, onBlank() — creates the blank draft (existing flow).
 */
const NewInvoiceDialog = ({ open, onClose, typingCenterId, thaklisEnabled, onBlank }) => {
  const navigate = useNavigate();
  const [source, setSource] = useState('WORK_ORDERS');
  const [org, setOrg] = useState(null);
  const [walkIn, setWalkIn] = useState(false);

  const { options, loading, onInputChange, filterOptions } = useOrganizationSearch({
    typingCenterId, enabled: open, mapOption: (o) => ({ id: o.id, name: o.name || o.organization_name }),
  });

  useEffect(() => {
    if (open) { setSource('WORK_ORDERS'); setOrg(null); setWalkIn(false); }
  }, [open]);

  const go = () => {
    if (source === 'WORK_ORDERS') {
      const q = new URLSearchParams(walkIn
        ? { tc: typingCenterId || '', walkIn: '1', orgName: 'Walk-in' }
        : { tc: typingCenterId || '', org: org.id, orgName: org.name || '' });
      onClose();
      navigate(`/invoices/new/items?${q.toString()}`);
    } else if (source === 'TICKETS') {
      onClose();
      navigate('/invoices/create');
    } else {
      onClose();
      onBlank();
    }
  };

  const canGo = source !== 'WORK_ORDERS' || walkIn || !!org;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>New Invoice</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>Start from</Typography>
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Option selected={source === 'WORK_ORDERS'} onClick={() => setSource('WORK_ORDERS')} icon={<WorkOrderIcon fontSize="small" />}
            title="Work orders" text="Bill a customer's completed work orders — all items, or only the ones you pick." />
          <Option selected={source === 'BLANK'} onClick={() => setSource('BLANK')} icon={<BlankIcon fontSize="small" />}
            title="Blank invoice" text="Add services from the catalog or type your own lines." />
          {thaklisEnabled && (
            <Option selected={source === 'TICKETS'} onClick={() => setSource('TICKETS')} icon={<TicketIcon fontSize="small" />}
              title="Thaklis tickets" text="Bill tickets processed on Thaklis." />
          )}
        </Box>

        {source === 'WORK_ORDERS' && (
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={options}
              loading={loading}
              disabled={walkIn}
              value={walkIn ? null : org}
              filterOptions={filterOptions}
              onInputChange={onInputChange}
              onChange={(e, v) => setOrg(v)}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              ListboxProps={{ sx: { '& .MuiAutocomplete-option': { fontSize: FS } } }}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Customer" required={!walkIn}
                  sx={{ '& .MuiInputBase-input': { fontSize: FS }, '& .MuiInputLabel-root': { fontSize: FS } }} />
              )}
            />
            <FormControlLabel
              sx={{ mt: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
              control={<Checkbox size="small" checked={walkIn} onChange={(e) => setWalkIn(e.target.checked)} />}
              label="Walk-in customer (work orders with no client)"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontSize: FS }}>Cancel</Button>
        <Button variant="contained" disabled={!canGo} onClick={go} sx={{ textTransform: 'none', fontSize: FS }}>Continue</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewInvoiceDialog;
